import type { GridColDef } from '@mui/x-data-grid';
import type { InvoiceOverview } from '@vy/api-definition';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import type { Account, InvoiceReviewStatus } from '@vy/types';
import { INVOICE_REVIEW_STATUSES, INVOICE_TYPES } from '@vy/types';
import type { ValueOf } from '@vy/utils';
import { isNotNullOrUndefined, raiseUnreachable, vyDate } from '@vy/utils';
import type { FieldPath } from 'react-hook-form';
import { ContractStatusChip } from '../../../../../../components/design/ContractStatusChip';
import { Price } from '../../../../../../components/design/Price';
import { formatDate } from '../../../../../../utils/date';
import { getBillingStatusLabel } from '../../InvoiceBillingStatusLabel';
import { getPartiallyPaidTooltipContent, isInvoicePartiallyPaid } from '../../invoice-partially-paid-utils';
import { MarketplaceRawResponseCell } from '../invoice-overview-table-components/MarketplaceRawResponseCell';
import { MarketplaceSyncStatusCell } from '../invoice-overview-table-components/MarketplaceSyncStatusCell';
import { MarketplaceUnitsCell } from '../invoice-overview-table-components/MarketplaceUnitsCell';
import { SyncStatusCell } from '../invoice-overview-table-components/SyncStatusCell';
import { InvoiceOverviewTableDotMenu } from '../InvoiceOverviewTableDotMenu';
import { InvoiceStatusSelect } from '../InvoiceStatusSelect';

export const INVOICES_COLUMNS_KEYS = Object.freeze({
  STATUS: 'billingStatus',
  WORKFLOW: 'reviewStatus',
  ID: 'id',
  DUE_DATE: 'dueDate',
  ISSUED_AT: 'issuedAt',
  CUSTOMER: 'customer.name',
  SUBSIDIARY: 'customer.subsidiaryId',
  INVOICE_NAME: 'name',
  TOTAL_AMOUNT: 'revenueBreakdown.total',
  CURRENCY: 'currency',
  SYNC: 'sync',
  MARKETPLACE_UNITS: 'marketplaceUnits',
  MARKETPLACE_SYNC: 'marketplaceSync',
  MARKETPLACE_RAW_RESPONSE: 'marketplaceRawResponse',
  CONTRACT_NAME: 'contractName',
  CONTRACT_STATUS: 'contractStatus',
  LAST_SENT_AT: 'lastSentAt',
  PURCHASE_ORDER: 'purchaseOrder',
  NOTES: 'notes',
  ACTION_MENU: 'actionMenu',
} satisfies Record<string, FieldPath<InvoiceOverview> | 'actionMenu' | 'sync' | 'marketplaceSync' | 'marketplaceUnits' | 'marketplaceRawResponse'>);

export type InvoicesColumnsKey = ValueOf<typeof INVOICES_COLUMNS_KEYS>;

type GetInvoicesOverviewTableColumnsOptions = {
  includeSyncColumn: boolean;
  includeMarketplaceColumns: boolean;
  issueOnEndOfMonth: boolean;
  subsidiaryNameById: Record<string, string>;
  defaultSubsidiary: string | undefined;
};

export function getInvoicesOverviewTableColumns(
  account: Account,
  options: GetInvoicesOverviewTableColumnsOptions,
): GridColDef<InvoiceOverview>[] {
  const { includeSyncColumn, includeMarketplaceColumns, issueOnEndOfMonth, subsidiaryNameById, defaultSubsidiary } =
    options;

  const resolveSubsidiaryName = (row: InvoiceOverview): string => {
    const { subsidiaryId } = row.customer;
    return (subsidiaryId ? subsidiaryNameById[subsidiaryId] : undefined) ?? defaultSubsidiary ?? '--';
  };

  const syncColumn: GridColDef<InvoiceOverview> = {
    field: INVOICES_COLUMNS_KEYS.SYNC,
    headerName: 'Sync',
    headerAlign: 'center',
    flex: 1,
    minWidth: 100,
    renderCell: (params) => {
      return <SyncStatusCell row={params.row} />;
    },
  };

  const marketplaceUnitsColumn: GridColDef<InvoiceOverview> = {
    field: INVOICES_COLUMNS_KEYS.MARKETPLACE_UNITS,
    headerName: 'MP Units',
    flex: 1,
    minWidth: 100,
    sortable: false,
    renderCell: (params) => {
      return <MarketplaceUnitsCell row={params.row} />;
    },
  };

  const marketplaceSyncColumn: GridColDef<InvoiceOverview> = {
    field: INVOICES_COLUMNS_KEYS.MARKETPLACE_SYNC,
    headerName: 'MP Sync',
    flex: 1,
    minWidth: 140,
    sortable: false,
    renderCell: (params) => <MarketplaceSyncStatusCell row={params.row} />,
  };

  const marketplaceRawResponseColumn: GridColDef<InvoiceOverview> = {
    field: INVOICES_COLUMNS_KEYS.MARKETPLACE_RAW_RESPONSE,
    headerName: 'MP Raw Response',
    flex: 1,
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }) => <MarketplaceRawResponseCell row={row} />,
  };

  return [
    {
      field: INVOICES_COLUMNS_KEYS.STATUS,
      valueGetter: (_value, row) => row.billingStatus,
      valueFormatter: (value) => getBillingStatusLabel(value),
      headerName: 'Status',
      headerClassName: 'pl-6 bold-header',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const { row } = params;
        const tooltipContent = getPartiallyPaidTooltipContent(row);
        const showIndicator = isInvoicePartiallyPaid(row);

        return (
          <InvoiceStatusSelect
            value={row.billingStatus}
            invoice={row}
            tooltipContent={tooltipContent}
            showIndicator={showIndicator}
          />
        );
      },
    },
    {
      field: INVOICES_COLUMNS_KEYS.TOTAL_AMOUNT,
      headerName: 'Amount',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) =>
        row.revenueBreakdown.total + row.revenueBreakdown.creditsUsed,
      renderHeader: () => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.totalAmountHeader)}>
          Amount
        </span>
      ),
      renderCell: (params) => {
        // Amount prior to credit redeem: `total` is stored post-credit, so add back creditsUsed.
        const amount = params.row.revenueBreakdown.total + params.row.revenueBreakdown.creditsUsed;
        const currency = params.row.currency;

        return (
          <span
            className="text-label font-sans"
            {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.totalAmount)}
          >
            <Price className="font-sans text-black" sx={{ fontSize: 14 }} currency={currency}>
              {amount}
            </Price>
          </span>
        );
      },
    },
    {
      field: INVOICES_COLUMNS_KEYS.CURRENCY,
      headerName: 'Currency',
      flex: 1,
      minWidth: 100,
      sortable: false,
      valueGetter: (_value, row) => row.currency,
      renderCell: (params) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.currency)}>
          {params.row.currency}
        </span>
      ),
    },
    {
      field: INVOICES_COLUMNS_KEYS.CUSTOMER,
      valueGetter: (_value, row) => row.customer.name,
      headerName: 'Customer',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.customer)}>
          {params.row.customer.name}
        </span>
      ),
    },
    {
      field: INVOICES_COLUMNS_KEYS.SUBSIDIARY,
      headerName: 'Subsidiary',
      flex: 1,
      minWidth: 130,
      sortable: false,
      valueGetter: (_value, row) => resolveSubsidiaryName(row),
      renderCell: ({ row }) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.subsidiary)}>
          {resolveSubsidiaryName(row)}
        </span>
      ),
    },
    {
      field: INVOICES_COLUMNS_KEYS.INVOICE_NAME,
      headerName: 'Period',
      minWidth: 80,
      valueGetter: (_value, row) => row.name,
      renderCell: (params) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.period)}>
          {params.row.name}
        </span>
      ),
    },
    {
      field: INVOICES_COLUMNS_KEYS.ISSUED_AT,
      headerName: 'Issue Date',
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => {
        const isIssueDateOnApproval = account.settings.invoicingSettings?.issueOnApproval;
        const isPostPayment = row.invoiceType === INVOICE_TYPES.Postpayment;
        const isDraft = row.reviewStatus === INVOICE_REVIEW_STATUSES.Draft;

        if (issueOnEndOfMonth && isPostPayment) {
          return (
            <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.issueDate)}>
              {formatDate(row.billingCycle.endTime)}
            </span>
          );
        }

        if (isNotNullOrUndefined(row.issuedAt)) {
          const formattedDate = formatDate(row.issuedAt);

          if (isIssueDateOnApproval && isDraft) {
            return (
              <span
                {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.issueDate)}
                style={{ color: '#78909C' }}
                className="flex items-center gap-1"
              >
                <VyIcon icon="PendingClock" strokeOpacity={1} />
                {formattedDate}
              </span>
            );
          }

          return (
            <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.issueDate)}>
              {formattedDate}
            </span>
          );
        }

        if (isNotNullOrUndefined(row.invoicingDate)) {
          return (
            <span
              {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.issueDate)}
              style={{ color: '#78909C' }}
              className="flex items-center gap-1"
            >
              <VyIcon icon="PendingClock" strokeOpacity={1} />
              {formatDate(row.invoicingDate)}
            </span>
          );
        }

        return <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.issueDate)}>--</span>;
      },
    },
    {
      field: INVOICES_COLUMNS_KEYS.DUE_DATE,
      headerName: 'Due Date',
      flex: 1,
      minWidth: 100,
      valueGetter: (_value, row) => row.dueDate,
      renderCell: ({ row }) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.dueDate)}>
          {isNotNullOrUndefined(row.dueDate) ? formatDate(vyDate(row.dueDate)) : '--'}
        </span>
      ),
    },
    {
      field: INVOICES_COLUMNS_KEYS.LAST_SENT_AT,
      headerName: 'Last Sent',
      flex: 1,
      minWidth: 110,
      valueGetter: (_value, row) => row.lastSentAt,
      renderCell: ({ row }) =>
        isNotNullOrUndefined(row.lastSentAt) ? formatDate(vyDate(row.lastSentAt)) : '--',
    },
    {
      field: INVOICES_COLUMNS_KEYS.ID,
      headerName: 'Invoice',
      flex: 1,
      minWidth: 90,
      valueGetter: (_value, row) => row.invoiceNumber,
      renderCell: (params) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.invoiceNumber)}>
          {params.row.invoiceNumber}
        </span>
      ),
    },
    {
      field: 'customerExternalId',
      headerName: 'Customer External Id',
      minWidth: 120,
      valueGetter: (_value, row) => row.customer.externalId,
    },
    {
      field: 'customerErpId',
      headerName: 'Customer ERP Id',
      minWidth: 120,
      valueGetter: (_value, row) => row.customer.erpMetadata?.erpId,
    },
    {
      field: 'customerCrmId',
      headerName: 'Customer CRM Id',
      minWidth: 120,
      valueGetter: (_value, row) => row.customer.crmMetadata?.crmId,
    },
    {
      field: INVOICES_COLUMNS_KEYS.WORKFLOW,
      headerName: 'Workflow',
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <div {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.workflow)}>
          {getReviewStatusLabel(row.reviewStatus)}
        </div>
      ),
    },
    ...(includeSyncColumn ? [syncColumn] : []),
    ...(includeMarketplaceColumns ? [marketplaceUnitsColumn, marketplaceSyncColumn, marketplaceRawResponseColumn] : []),
    {
      field: INVOICES_COLUMNS_KEYS.CONTRACT_NAME,
      headerName: 'Contract',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.contractName)}>
          {params.row.contractName ?? '--'}
        </span>
      ),
    },
    {
      field: INVOICES_COLUMNS_KEYS.CONTRACT_STATUS,
      headerName: 'Contract Status',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.contractStatus)}>
          {row.contractStatus ? (
            <ContractStatusChip status={row.contractStatus} endDate={row.contractEndDate} />
          ) : (
            '--'
          )}
        </span>
      ),
    },
    {
      field: INVOICES_COLUMNS_KEYS.PURCHASE_ORDER,
      headerName: 'PO#',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => row.purchaseOrder ?? '--',
      renderCell: (params) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.purchaseOrder)}>
          {params.row.purchaseOrder ?? '--'}
        </span>
      ),
    },
    {
      field: INVOICES_COLUMNS_KEYS.NOTES,
      headerName: 'Memo',
      flex: 1,
      minWidth: 200,
      valueGetter: (_value, row) => row.notes ?? '--',
      renderCell: (params) => (
        <span {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.memo)}>
          {params.row.notes ?? '--'}
        </span>
      ),
    },
    {
      field: INVOICES_COLUMNS_KEYS.ACTION_MENU,
      headerName: '',
      width: 30,
      disableExport: true,
      sortable: false,
      headerClassName: 'bg-white',
      cellClassName: 'bg-white',
      renderCell: ({ row }) => <InvoiceOverviewTableDotMenu invoice={row} />,
    },
  ];
}

export function getReviewStatusLabel(reviewStatus: InvoiceReviewStatus) {
  switch (reviewStatus) {
    case INVOICE_REVIEW_STATUSES.Approved:
      return 'Approved';
    case INVOICE_REVIEW_STATUSES.InReview:
      return 'In review';
    case INVOICE_REVIEW_STATUSES.Draft:
      return 'Draft';
    case INVOICE_REVIEW_STATUSES.Future:
      return 'Future';
    default:
      return raiseUnreachable(reviewStatus);
  }
}

